import { useEffect, memo, useState, useMemo, useCallback, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import ImagePlaceholder from '../Global/ImagePlaceholder';
import { fetchMembers, addMemberOptimistic, removeMemberOptimistic, addMember, removeMember } from '../../redux/Slices/memberSlice';
import './scss/MembersPopover.scss';
import { fetchActivities } from '../../redux/Slices/activitiesSlice';
import MemberOnlineStatus from '../Global/MemberOnlineStatus';
import LastActive from '../Global/LastActive';
import { config } from '../../config';
import { fetchWithCSRF } from '../../utils/apiUtils';


// Memoized MemberItem component
const MemberItem = memo(({ member, action, handleAction }) => (
    <div className="members-popover__member" onClick={(e) => handleAction(e, member)}>
        {member.profilePicture ? (

            <div className="image-placeholder-container">
                <img
                    src={member.profilePicture}
                    alt={member.username}
                    className="members-popover__avatar"
                    loading="lazy"
                    style={{
                        filter: member.userStatus === 'inactive' ? 'grayscale(100%)' : 'none'
                    }}
                />
                {member.userStatus === 'inactive' && (
                    <span className="inactive-indicator" title="Inactive user">
                        <span className="material-symbols-outlined">schedule</span>
                    </span>
                )}
                {member.userStatus !== 'inactive' && <MemberOnlineStatus member={member} />}
            </div>

        ) : (
            <ImagePlaceholder size={30} member={member} text={member.username} fontSize="14px" />
        )}
        <div className="member-info">
            <span className="member-name">{member.username}</span>
        </div>
        <div className="action-icon">
            <span className="material-symbols-outlined">
                {action === 'ADD' ? 'close' : 'add'}
            </span>
        </div>
    </div>
));

// Main MembersPopover component
const MembersPopover = () => {
    const dispatch = useDispatch();
    const { card } = useSelector((state) => state.card);
    const { boardMembers, cardMembers } = useSelector((state) => state.member) || [];

    const [searchQuery, setSearchQuery] = useState('');
    const [debouncedSearchQuery, setDebouncedSearchQuery] = useState('');
    const searchInputRef = useRef(null);
    const [serverBoardResults, setServerBoardResults] = useState(null);
    const [serverLoading, setServerLoading] = useState(false);
    const lastQueryRef = useRef('');


    const cardId = card?.shortId || '';

    // Focus on search input when component mounts
    useEffect(() => {
        if (searchInputRef.current) {
            searchInputRef.current.focus();
        }
    }, []);

    // Debounce search query
    useEffect(() => {
        const timer = setTimeout(() => {
            setDebouncedSearchQuery(searchQuery);
        }, 300);

        return () => clearTimeout(timer);
    }, [searchQuery]);

    // Filter available board members (not in card)
    const availableBoardMembersAll = useMemo(() => {
        return boardMembers.filter(
            (member) => !cardMembers.some((cardMember) => cardMember._id === member._id)
        );
    }, [boardMembers, cardMembers]);

    // Local filtering based on debounced search (does not mutate global store)
    const normalizedQuery = debouncedSearchQuery.trim().toLowerCase();

    const filteredCardMembers = useMemo(() => {
        if (!normalizedQuery) return cardMembers;
        return cardMembers.filter((m) => {
            const name = (m.name || '').toLowerCase();
            const username = (m.username || '').toLowerCase();
            return name.includes(normalizedQuery) || username.includes(normalizedQuery);
        });
    }, [cardMembers, normalizedQuery]);

    const filteredAvailableBoardMembers = useMemo(() => {
        if (!normalizedQuery) return availableBoardMembersAll;
        return availableBoardMembersAll.filter((m) => {
            const name = (m.name || '').toLowerCase();
            const username = (m.username || '').toLowerCase();
            return name.includes(normalizedQuery) || username.includes(normalizedQuery);
        });
    }, [availableBoardMembersAll, normalizedQuery]);

    // Server-side search for board members only (isolated from Redux)
    useEffect(() => {
        if (!normalizedQuery) {
            setServerBoardResults(null);
            lastQueryRef.current = '';
            return;
        }
        if (!card?.board) return;

        const query = normalizedQuery;
        lastQueryRef.current = query;
        setServerLoading(true);

        (async () => {
            try {
                const url = `${config.API_URI}/v1/members/board/${card.board}?search=${encodeURIComponent(query)}`;
                const response = await fetchWithCSRF(url, {
                    headers: {
                        'Accept': 'application/json',
                        'Content-Type': 'application/json',
                    }
                });
                if (!response?.ok) {
                    throw new Error('Failed to search members');
                }
                const data = await response.json();
                if (lastQueryRef.current === query) {
                    setServerBoardResults(Array.isArray(data) ? data : []);
                }
            } catch (err) {
                if (lastQueryRef.current === query) {
                    setServerBoardResults([]);
                }
            } finally {
                if (lastQueryRef.current === query) {
                    setServerLoading(false);
                }
            }
        })();
    }, [normalizedQuery, card?.board]);

    const availableBoardMembersFromServer = useMemo(() => {
        if (!serverBoardResults) return null;
        return serverBoardResults.filter(
            (member) => !cardMembers.some((cardMember) => cardMember._id === member._id)
        );
    }, [serverBoardResults, cardMembers]);

    const displayedBoardMembers = useMemo(() => {
        if (normalizedQuery) {
            return (availableBoardMembersFromServer ?? filteredAvailableBoardMembers) || [];
        }
        return availableBoardMembersAll;
    }, [normalizedQuery, availableBoardMembersFromServer, filteredAvailableBoardMembers, availableBoardMembersAll]);


    // Fetch board members when component mounts
    useEffect(() => {
        if (card?.board) {
            dispatch(fetchMembers({
                type: 'board',
                id: card.board
            }));
        }
    }, [card?.board, dispatch]);

    // Fetch card members when component mounts
    useEffect(() => {
        if (cardId) {
            dispatch(fetchMembers({
                type: 'card',
                id: cardId
            }));
        }
    }, [cardId, dispatch]);

    const handleSearchChange = useCallback((e) => {
        setSearchQuery(e.target.value);
    }, []);

    const handleAddMember = async (e, member) => {
        e.preventDefault();
        const memberId = member._id;


        // Optimistic UI Update
        dispatch(addMemberOptimistic({ cardId, member }));

        try {
            // Server Request
            const response = await dispatch(addMember({ cardId, memberId })).unwrap();

            if (response.error) {
                console.error('Member already added:', response.error);
            } else {
                dispatch(fetchActivities({ cardId }));

                // Send Push Notification
                // sendPushNotification({
                //     memberId: member._id,
                //     type: 'ADD_MEMBER_TO_CARD',
                //     data: { cardId }, // Optional extra data
                // });
            }
        } catch (error) {
            console.error('Failed to add member:', error);
        }
    };

    const handleRemoveMember = async (e, member) => {
        e.preventDefault();
        const memberId = member._id;

        // Optimistic UI Update
        dispatch(removeMemberOptimistic({ cardId, memberId }));

        try {
            // Server Request
            const response = await dispatch(removeMember({ cardId, memberId })).unwrap();

            // Check backend response
            if (response.error) {
                console.error('Member not removed:', response.error);
            } else {
                dispatch(fetchActivities({ cardId }));

                // Send Push Notification
                // sendPushNotification({
                //     memberId: member._id,
                //     type: 'REMOVE_MEMBER_FROM_CARD',
                //     data: { cardId }, // Optional extra data
                // });

            }
        } catch (error) {
            console.error('Failed to remove member:', error);
        }
    };

    return (
        <div className="members-popover">
            <div className="members-popover__header">
                <h3>Members</h3>
            </div>
            <input
                ref={searchInputRef}
                type="text"
                className="members-popover__search"
                placeholder="Search members"
                value={searchQuery}
                onChange={handleSearchChange}
            />
            <div className='members-list-container'>
                {filteredCardMembers.length > 0 && (
                    <div className="members-popover__section">
                        <h4>Card members</h4>
                        {filteredCardMembers.map((member) => (
                            <MemberItem key={member._id} member={member} action="ADD" handleAction={handleRemoveMember} />
                        ))}
                    </div>
                )}
                {displayedBoardMembers.length > 0 && (
                    <div className="members-popover__section">
                        <h4>Board members</h4>
                        {normalizedQuery && serverLoading && (
                            <div className="members-popover__loading">Searching...</div>
                        )}
                        {displayedBoardMembers.map((member) => (
                            <MemberItem key={member._id} member={member} action="REMOVE" handleAction={handleAddMember} />
                        ))}
                    </div>
                )}
                {searchQuery.trim() && filteredCardMembers.length === 0 && displayedBoardMembers.length === 0 && (
                    <div className="members-popover__no-results">
                        <p>No members found matching "{searchQuery}"</p>
                    </div>
                )}
            </div>
        </div>
    );
};

export default MembersPopover;
